import { useLazyQuery } from "@apollo/client";
import { IonSkeletonText } from "@ionic/react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import styles from "@components/carePlan/ExpertCarePlan.module.css";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import SearchInput from "@components/fieldsNative/SearchInput";
import HandleQuery from "@components/HandleQuery";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener, { Opener } from "@hooks/useOpener";
import {
  ArticleContentTypeEnum,
  ArticleSortByEnum,
  CarePlanTaskTemplateSortByEnum,
  GetArticlesSearchDocument,
  GetArticlesSearchQuery,
  GetArticlesSearchQueryVariables,
  GetArticlesWithTitleDocument,
  GetArticlesWithTitleQuery,
  GetArticlesWithTitleQueryVariables,
  GetCarePlanTaskTemplatesBasicDocument,
  GetCarePlanTaskTemplatesBasicQuery,
  GetCarePlanTaskTemplatesBasicQueryVariables,
  GetCarePlanTaskTemplatesSearchDocument,
  GetCarePlanTaskTemplatesSearchQuery,
  GetCarePlanTaskTemplatesSearchQueryVariables,
  GetEmployerBenefitsDocument,
  GetEmployerBenefitsQuery,
  GetEmployerBenefitsQueryVariables,
  GetEmployerBenefitsSearchDocument,
  GetEmployerBenefitsSearchQuery,
  GetEmployerBenefitsSearchQueryVariables,
  GetGoalTemplatesBasicDocument,
  GetGoalTemplatesBasicQuery,
  GetGoalTemplatesBasicQueryVariables,
  GetGoalTemplatesSearchDocument,
  GetGoalTemplatesSearchQuery,
  GetGoalTemplatesSearchQueryVariables,
  GetWorksheetTemplatesBasicDocument,
  GetWorksheetTemplatesBasicQuery,
  GetWorksheetTemplatesBasicQueryVariables,
  GetWorksheetTemplatesSearchDocument,
  GetWorksheetTemplatesSearchQuery,
  GetWorksheetTemplatesSearchQueryVariables,
  GoalItemTypeEnum,
  GoalTemplateSortByEnum,
  SortDirectionEnum,
  UserTypeEnum,
  WorksheetTemplateSortByEnum
} from "@typing/Generated";
import { countryNameForCode } from "@utils/countryUtils";
import { articleHasVideo } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { joinListWithCommasAndAnd } from "@utils/stringUtils";

type Item = {
  highlight?: string | null;
  id: string;
  routerLink?: string;
  title: string;
  video?: boolean;
  webinar?: boolean;
};

type GroupProps = {
  addedIds?: string[];
  canSearchMore: boolean;
  generateChangeHandler: (id: string, title: string) => (checked: boolean) => void;
  incrementSearchCount: () => void;
  items: Item[];
  searchLoading: boolean;
};

const Group = ({
  addedIds,
  canSearchMore,
  generateChangeHandler,
  incrementSearchCount,
  items,
  searchLoading
}: GroupProps) => {
  const intl = useIntl();

  return (
    <div>
      {searchLoading ? (
        <IonSkeletonText animated className={styles.loader} />
      ) : (
        <div className={styles.rowItems}>
          {items.map(item => {
            const purifiedHighlight =
              // eslint-disable-next-line @typescript-eslint/naming-convention
              item.highlight && parse(DOMPurify.sanitize(item.highlight, { ALLOWED_TAGS: ["em"] }));

            return (
              <div key={item.id}>
                <div className={styles.rowItem}>
                  <ToggleCheckbox
                    checked={addedIds ? addedIds.includes(item.id) : false}
                    name="add"
                    onChange={generateChangeHandler(item.id, item.title)}
                  />
                  <div className={styles.iconAndTitle}>
                    {item.webinar ? (
                      <div className={styles.videoCircle}>
                        <Icon
                          hoverContent={intl.formatMessage({ id: "accessibility.ariaLabels.webinar" })}
                          icon="videoPlus"
                        />
                      </div>
                    ) : item.video ? (
                      <div className={styles.videoCircle}>
                        <Icon
                          hoverContent={intl.formatMessage({ id: "accessibility.ariaLabels.video" })}
                          icon="playCircle"
                        />
                      </div>
                    ) : null}
                    <BodyCopy>{item.title}</BodyCopy>
                  </div>

                  {item.routerLink && (
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.open"
                      icon="arrowUpRightSquare"
                      routerLink={item.routerLink}
                    />
                  )}
                </div>
                <div className={styles.highlight}>{purifiedHighlight}</div>
              </div>
            );
          })}
          {canSearchMore && (
            <Button fill="clear" onClick={incrementSearchCount}>
              <FormattedMessage id="pages.carePlan.base.goals.searchMore" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

type Props = {
  addedIds?: string[];
  handleAdd: (id: string, title: string) => void;
  handleRemove: (id: string, title: string) => void;
  itemType: GoalItemTypeEnum | "GOAL";
  journeyId: string;
  opener: Opener;
  skipCloseOnSubmit?: boolean;
};

const ItemBrowser = ({
  addedIds = [],
  handleAdd,
  handleRemove,
  itemType,
  journeyId,
  opener,
  skipCloseOnSubmit = false
}: Props) => {
  const expandOpener = useOpener();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const [search, setSearch] = useState("");
  const [searchCount, setSearchCount] = useState(25);

  const incrementSearchCount = () => setSearchCount(v => v + 25);

  const [getCarePlanTaskTemplates, carePlanTaskTemplatesQuery] = useLazyQuery<
    GetCarePlanTaskTemplatesBasicQuery,
    GetCarePlanTaskTemplatesBasicQueryVariables
  >(GetCarePlanTaskTemplatesBasicDocument);

  const [getGoalTemplates, goalTemplatesQuery] = useLazyQuery<
    GetGoalTemplatesBasicQuery,
    GetGoalTemplatesBasicQueryVariables
  >(GetGoalTemplatesBasicDocument);

  const [getArticles, articlesQuery] = useLazyQuery<GetArticlesWithTitleQuery, GetArticlesWithTitleQueryVariables>(
    GetArticlesWithTitleDocument
  );

  const [getArticleSearch, getArticlesSearchQuery] = useLazyQuery<
    GetArticlesSearchQuery,
    GetArticlesSearchQueryVariables
  >(GetArticlesSearchDocument, {
    fetchPolicy: "no-cache"
  });

  const [getCarePlanTaskTemplatesSearch, getCarePlanTaskTemplatesSearchQuery] = useLazyQuery<
    GetCarePlanTaskTemplatesSearchQuery,
    GetCarePlanTaskTemplatesSearchQueryVariables
  >(GetCarePlanTaskTemplatesSearchDocument, {
    fetchPolicy: "no-cache"
  });

  const [getEmployerBenefitsSearch, getEmployerBenefitsSearchQuery] = useLazyQuery<
    GetEmployerBenefitsSearchQuery,
    GetEmployerBenefitsSearchQueryVariables
  >(GetEmployerBenefitsSearchDocument, {
    fetchPolicy: "no-cache"
  });

  const [getGoalTemplatesSearch, getGoalTemplatesSearchQuery] = useLazyQuery<
    GetGoalTemplatesSearchQuery,
    GetGoalTemplatesSearchQueryVariables
  >(GetGoalTemplatesSearchDocument, {
    fetchPolicy: "no-cache"
  });

  const [getWorksheetTemplatesSearch, getWorksheetTemplatesSearchQuery] = useLazyQuery<
    GetWorksheetTemplatesSearchQuery,
    GetWorksheetTemplatesSearchQueryVariables
  >(GetWorksheetTemplatesSearchDocument, {
    fetchPolicy: "no-cache"
  });

  const canSearchMore = useMemo(() => {
    if (searchCount >= 100) return false;

    if (itemType === GoalItemTypeEnum.ARTICLE) {
      return !!(getArticlesSearchQuery.data?.articlesSearch.length === searchCount && search);
    } else if (itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM || itemType === GoalItemTypeEnum.TASK) {
      return !!(getCarePlanTaskTemplatesSearchQuery.data?.carePlanTaskTemplatesSearch.length === searchCount && search);
    } else if (itemType === GoalItemTypeEnum.EMPLOYER_BENEFIT) {
      return !!(getEmployerBenefitsSearchQuery.data?.employerBenefitsSearch.length === searchCount && search);
    } else if (itemType === "GOAL") {
      return !!(getGoalTemplatesSearchQuery.data?.goalTemplatesSearch.length === searchCount && search);
    } else if (itemType === GoalItemTypeEnum.WORKSHEET) {
      return !!(getWorksheetTemplatesSearchQuery.data?.worksheetTemplatesSearch.length === searchCount && search);
    }

    return false;
  }, [
    getArticlesSearchQuery.data?.articlesSearch.length,
    getCarePlanTaskTemplatesSearchQuery.data?.carePlanTaskTemplatesSearch.length,
    getEmployerBenefitsSearchQuery.data?.employerBenefitsSearch.length,
    getGoalTemplatesSearchQuery.data?.goalTemplatesSearch.length,
    getWorksheetTemplatesSearchQuery.data?.worksheetTemplatesSearch.length,
    itemType,
    search,
    searchCount
  ]);

  useEffect(() => {
    if (search && search !== "" && itemType === GoalItemTypeEnum.ARTICLE) {
      getArticleSearch({
        variables: {
          contentTypes: [
            ArticleContentTypeEnum.MY_STORY,
            ArticleContentTypeEnum.TEXT,
            ArticleContentTypeEnum.VIDEO,
            ArticleContentTypeEnum.WEBINAR
          ],
          count: searchCount,
          search
        }
      });
    }
  }, [getArticleSearch, itemType, search, searchCount]);

  useEffect(() => {
    if (search && search !== "" && itemType === GoalItemTypeEnum.EMPLOYER_BENEFIT) {
      getEmployerBenefitsSearch({
        variables: {
          count: searchCount,
          journeyId,
          search
        }
      });
    }
  }, [getArticleSearch, getEmployerBenefitsSearch, itemType, journeyId, search, searchCount]);

  useEffect(() => {
    if (search && search !== "" && itemType === GoalItemTypeEnum.WORKSHEET) {
      getWorksheetTemplatesSearch({
        variables: {
          count: searchCount,
          search
        }
      });
    }
  }, [
    getArticleSearch,
    getEmployerBenefitsSearch,
    getWorksheetTemplatesSearch,
    itemType,
    journeyId,
    search,
    searchCount
  ]);

  useEffect(() => {
    if (
      search &&
      search !== "" &&
      (itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM || itemType === GoalItemTypeEnum.TASK)
    ) {
      getCarePlanTaskTemplatesSearch({
        variables: {
          count: searchCount,
          search,
          userType: itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM ? UserTypeEnum.CLIENT : UserTypeEnum.EXPERT
        }
      });
    }
  }, [getCarePlanTaskTemplatesSearch, itemType, search, searchCount]);

  useEffect(() => {
    if (search && search !== "" && itemType === "GOAL") {
      getGoalTemplatesSearch({
        variables: {
          count: searchCount,
          search
        }
      });
    }
  }, [getArticleSearch, getGoalTemplatesSearch, itemType, search, searchCount]);

  const [getEmployerBenefits, employerBenefitsQuery] = useLazyQuery<
    GetEmployerBenefitsQuery,
    GetEmployerBenefitsQueryVariables
  >(GetEmployerBenefitsDocument);

  const [getWorksheetTemplates, worksheetTemplatesQuery] = useLazyQuery<
    GetWorksheetTemplatesBasicQuery,
    GetWorksheetTemplatesBasicQueryVariables
  >(GetWorksheetTemplatesBasicDocument);

  useEffect(() => {
    if (!opener.isOpen) {
      setSearchCount(25);
    }
  }, [opener.isOpen]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (itemType === "GOAL") {
      getGoalTemplates({
        variables: {
          sortBy: GoalTemplateSortByEnum.TITLE,
          sortDirection: SortDirectionEnum.ASCENDING
        }
      });
    } else if (itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM || itemType === GoalItemTypeEnum.TASK) {
      getCarePlanTaskTemplates({
        variables: {
          sortBy: CarePlanTaskTemplateSortByEnum.TITLE,
          sortDirection: SortDirectionEnum.ASCENDING,
          userType: itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM ? UserTypeEnum.CLIENT : UserTypeEnum.EXPERT
        }
      });
    } else if (itemType === GoalItemTypeEnum.ARTICLE) {
      getArticles({
        variables: {
          contentTypes: [
            ArticleContentTypeEnum.MY_STORY,
            ArticleContentTypeEnum.TEXT,
            ArticleContentTypeEnum.VIDEO,
            ArticleContentTypeEnum.WEBINAR
          ],
          sortBy: ArticleSortByEnum.TITLE,
          sortDirection: SortDirectionEnum.ASCENDING
        }
      });
    } else if (itemType === GoalItemTypeEnum.EMPLOYER_BENEFIT) {
      getEmployerBenefits({ variables: { journeyId } });
    } else {
      getWorksheetTemplates({
        variables: {
          sortBy: WorksheetTemplateSortByEnum.TITLE
        }
      });
    }
  }, [
    getArticles,
    getCarePlanTaskTemplates,
    getEmployerBenefits,
    getGoalTemplates,
    getWorksheetTemplates,
    itemType,
    journeyId,
    opener.isOpen
  ]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (
      itemType === GoalItemTypeEnum.ARTICLE &&
      !articlesQuery.loading &&
      articlesQuery.data?.articles.pageInfo.hasNextPage
    ) {
      articlesQuery.fetchMore({
        variables: {
          cursor: articlesQuery.data.articles.pageInfo.endCursor
        }
      });
    }
  }, [articlesQuery, itemType, opener.isOpen]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (
      (itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM || itemType === GoalItemTypeEnum.TASK) &&
      !carePlanTaskTemplatesQuery.loading &&
      carePlanTaskTemplatesQuery.data?.carePlanTaskTemplates.pageInfo.hasNextPage
    ) {
      carePlanTaskTemplatesQuery.fetchMore({
        variables: {
          cursor: carePlanTaskTemplatesQuery.data.carePlanTaskTemplates.pageInfo.endCursor
        }
      });
    }
  }, [carePlanTaskTemplatesQuery, itemType, opener.isOpen]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (
      itemType === GoalItemTypeEnum.EMPLOYER_BENEFIT &&
      !employerBenefitsQuery.loading &&
      employerBenefitsQuery.data?.employerBenefits.pageInfo.hasNextPage
    ) {
      employerBenefitsQuery.fetchMore({
        variables: {
          cursor: employerBenefitsQuery.data.employerBenefits.pageInfo.endCursor,
          journeyId
        }
      });
    }
  }, [articlesQuery, employerBenefitsQuery, itemType, journeyId, opener.isOpen]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (
      itemType === "GOAL" &&
      !goalTemplatesQuery.loading &&
      goalTemplatesQuery.data?.goalTemplates.pageInfo.hasNextPage
    ) {
      goalTemplatesQuery.fetchMore({
        variables: {
          cursor: goalTemplatesQuery.data.goalTemplates.pageInfo.endCursor
        }
      });
    }
  }, [goalTemplatesQuery, itemType, opener.isOpen]);

  useEffect(() => {
    if (!opener.isOpen) return;

    if (
      itemType === GoalItemTypeEnum.WORKSHEET &&
      !worksheetTemplatesQuery.loading &&
      worksheetTemplatesQuery.data?.worksheetTemplates.pageInfo.hasNextPage
    ) {
      worksheetTemplatesQuery.fetchMore({
        variables: {
          cursor: worksheetTemplatesQuery.data.worksheetTemplates.pageInfo.endCursor
        }
      });
    }
  }, [worksheetTemplatesQuery, itemType, opener.isOpen]);

  const articles = useNodesFromConnection(articlesQuery.data?.articles);
  const articleItems = useMemo(() => {
    if (search) {
      return (
        getArticlesSearchQuery.data?.articlesSearch.map(result => ({
          ...result,
          routerLink: journeyRoute({
            journeyId,
            linkAfterJourney: `/articles/${result.id}`,
            myExpert
          }),
          video: articleHasVideo(result),
          webinar: result.contentType === ArticleContentTypeEnum.WEBINAR
        })) ?? []
      );
    }
    return articles.map(article => ({
      ...article,
      routerLink: journeyRoute({
        journeyId,
        linkAfterJourney: `/articles/${article.id}`,
        myExpert
      }),
      video: articleHasVideo(article),
      webinar: article.contentType === ArticleContentTypeEnum.WEBINAR
    }));
  }, [getArticlesSearchQuery.data?.articlesSearch, articles, journeyId, myExpert, search]);

  const carePlanTaskTemplates = useNodesFromConnection(carePlanTaskTemplatesQuery.data?.carePlanTaskTemplates);
  const carePlanTaskTemplateItems = useMemo(() => {
    if (search) {
      return getCarePlanTaskTemplatesSearchQuery.data?.carePlanTaskTemplatesSearch ?? [];
    }
    return carePlanTaskTemplates;
  }, [search, carePlanTaskTemplates, getCarePlanTaskTemplatesSearchQuery.data?.carePlanTaskTemplatesSearch]);

  const employerBenefits = useNodesFromConnection(employerBenefitsQuery.data?.employerBenefits);
  const employerBenefitItems = useMemo(() => {
    if (search) {
      return (
        getEmployerBenefitsSearchQuery.data?.employerBenefitsSearch.map(eb => ({
          ...eb,
          title: `${eb.vendorName} - ${joinListWithCommasAndAnd(
            eb.countryCodes.map(countryCode => countryNameForCode(countryCode)),
            intl
          )}`
        })) ?? []
      );
    }
    return employerBenefits.map(eb => ({
      ...eb,
      title: eb.vendorName
    }));
  }, [employerBenefits, getEmployerBenefitsSearchQuery.data?.employerBenefitsSearch, intl, search]);

  const goalTemplates = useNodesFromConnection(goalTemplatesQuery.data?.goalTemplates);
  const goalTemplateItems = useMemo(() => {
    if (search) {
      return getGoalTemplatesSearchQuery.data?.goalTemplatesSearch ?? [];
    }
    return goalTemplates;
  }, [getGoalTemplatesSearchQuery.data?.goalTemplatesSearch, goalTemplates, search]);

  const worksheetTemplates = useNodesFromConnection(worksheetTemplatesQuery.data?.worksheetTemplates);
  const worksheetTemplateItems = useMemo(() => {
    if (search) {
      return (
        getWorksheetTemplatesSearchQuery.data?.worksheetTemplatesSearch.map(wt => ({
          ...wt,
          routerLink: journeyRoute({
            journeyId,
            linkAfterJourney: `/worksheet-templates/${wt.id}`,
            myExpert
          })
        })) ?? []
      );
    }
    return worksheetTemplates;
  }, [
    getWorksheetTemplatesSearchQuery.data?.worksheetTemplatesSearch,
    journeyId,
    myExpert,
    search,
    worksheetTemplates
  ]);

  const generateChangeHandler = (id: string, title: string) => (checked: boolean) => {
    if (!skipCloseOnSubmit) {
      opener.close();
    }

    if (checked) {
      handleAdd(id, title);
    } else {
      handleRemove(id, title);
    }
  };

  return (
    <div className={styles.itemBrowserContainer}>
      <div className={styles.relativeOpenerContainer}>
        <div className={styles.browserOpener}>
          <Button color="gray-500" fill="clear" onClick={opener.open}>
            <FormattedMessage id={`models.goalItemTypes.add.${itemType}`} />
          </Button>
        </div>
      </div>

      <div
        className={
          opener.isOpen && expandOpener.isOpen
            ? styles.itemBrowserExpanded
            : opener.isOpen
              ? styles.itemBrowser
              : "ion-hide"
        }
      >
        <div className={styles.browserTitle}>
          <Heading2>
            <FormattedMessage id={`models.goalItemTypes.title.${itemType}`} />
          </Heading2>
          <div className={styles.flex}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.expand"
              icon={expandOpener.isOpen ? "compress" : "expand"}
              onClick={expandOpener.toggle}
              size="medium"
            />
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.close"
              icon="times"
              onClick={opener.close}
              size="medium"
            />
          </div>
        </div>
        <div className={styles.search}>
          <SearchInput error={""} initialValue="" name="search" setValue={v => setSearch(v)} />
        </div>

        <div className={styles.itemBrowserScroll}>
          {itemType === GoalItemTypeEnum.ARTICLE && (
            <HandleQuery loadingContent={<IonSkeletonText animated className={styles.loader} />} query={articlesQuery}>
              {() => (
                <Group
                  addedIds={addedIds}
                  canSearchMore={canSearchMore}
                  generateChangeHandler={generateChangeHandler}
                  incrementSearchCount={incrementSearchCount}
                  items={articleItems}
                  searchLoading={!!(getArticlesSearchQuery.loading && search)}
                />
              )}
            </HandleQuery>
          )}

          {(itemType === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM || itemType === GoalItemTypeEnum.TASK) && (
            <HandleQuery
              loadingContent={<IonSkeletonText animated className={styles.loader} />}
              query={carePlanTaskTemplatesQuery}
            >
              {() => (
                <Group
                  addedIds={addedIds}
                  canSearchMore={canSearchMore}
                  generateChangeHandler={generateChangeHandler}
                  incrementSearchCount={incrementSearchCount}
                  items={carePlanTaskTemplateItems}
                  searchLoading={!!(getCarePlanTaskTemplatesSearchQuery.loading && search)}
                />
              )}
            </HandleQuery>
          )}

          {itemType === GoalItemTypeEnum.EMPLOYER_BENEFIT && (
            <HandleQuery
              loadingContent={<IonSkeletonText animated className={styles.loader} />}
              query={employerBenefitsQuery}
            >
              {() => (
                <Group
                  addedIds={addedIds}
                  canSearchMore={canSearchMore}
                  generateChangeHandler={generateChangeHandler}
                  incrementSearchCount={incrementSearchCount}
                  items={employerBenefitItems}
                  searchLoading={!!(getEmployerBenefitsSearchQuery.loading && search)}
                />
              )}
            </HandleQuery>
          )}

          {itemType === "GOAL" && (
            <HandleQuery
              loadingContent={<IonSkeletonText animated className={styles.loader} />}
              query={goalTemplatesQuery}
            >
              {() => (
                <Group
                  addedIds={addedIds}
                  canSearchMore={canSearchMore}
                  generateChangeHandler={generateChangeHandler}
                  incrementSearchCount={incrementSearchCount}
                  items={goalTemplateItems}
                  searchLoading={!!(getGoalTemplatesSearchQuery.loading && search)}
                />
              )}
            </HandleQuery>
          )}

          {itemType === GoalItemTypeEnum.WORKSHEET && (
            <HandleQuery
              loadingContent={<IonSkeletonText animated className={styles.loader} />}
              query={worksheetTemplatesQuery}
            >
              {() => (
                <Group
                  addedIds={addedIds}
                  canSearchMore={canSearchMore}
                  generateChangeHandler={generateChangeHandler}
                  incrementSearchCount={incrementSearchCount}
                  items={worksheetTemplateItems}
                  searchLoading={!!(getWorksheetTemplatesSearchQuery.loading && search)}
                />
              )}
            </HandleQuery>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemBrowser;
